/**
 * Proposta de LOCAÇÃO (DEMO-03, telas/PropostaLocacao.tsx) — preço FECHADO pelo
 * parceiro; a comissão está DENTRO. Ordem: descrição do projeto → itens e
 * componentes (PN/SKU obrigatório, pelo catálogo) → propostas de parceiros
 * (comparar e selecionar) → condições financeiras e regra de comissão (herdada
 * do parceiro; alteração autorizada 🔒, conferida e auditada no servidor) →
 * Condições de Locação (herdadas do parceiro e editáveis nesta versão) → visão interna.
 */
import { useState } from 'react';
import { api } from '../../api.ts';
import { useSessao } from '../../sessao.tsx';
import { Ajuda, Estado, Modal, Nota, ResumoIndicadores } from '../ui.tsx';
import { CampoData } from '../CampoData.tsx';
import { EditorReajuste } from '../Reajuste.tsx';
import { CAMPOS_CONDICOES_LOCACAO, textoReajuste } from '../../../../backend/src/compartilhado/comercial.ts';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import {
  PERIODICIDADES,
  REGRAS_COMISSAO,

  calcularLocacao,
  calculoLocacao,
  comReajuste,

  entidadeDe,

  grupoDe,
  liquidaEstimada,
  abatimentoEfetivo,
  mesmoReajuste,
  reajusteDaLocacao,
  reajusteDaOferta,
  regraComissao,
  type Base,
  type ComponenteOperacao,
  type DadosLocacao,
  type GrupoRef,
  type IdRegraComissao,
  type OfertaLocacao,
  type Periodicidade,
  type ProdutoRef,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { ProdutoNaLinha } from './ProdutoNaLinha.tsx';
import { agoraSaoPaulo, useProposta, type DadosProposta } from './contexto.tsx';
import { moeda2, pctNum } from './formatos.ts';

const RESPONSAVEIS_OPERACAO = ['Parceiro de Locação', 'INTTRIX', 'Cliente', 'A definir'];
let seq = 0;

/** Componente de Locação a partir do cadastro (PN/SKU → descrição, fabricante, pilar, tipo). */
export const componenteDoProduto = (p: ProdutoRef, base: Partial<ComponenteOperacao> = {}): ComponenteOperacao => ({
  id: base.id ?? `cp${Date.now() % 100000}-${++seq}`,
  descricao: p.nome,
  categoria: p.categoria,
  fabricante: p.fabricante,
  modelo: base.modelo ?? '',
  quantidade: base.quantidade ?? 1,
  condicao: '',
  ano: '',
  lote: '',
  resultado: 'pendente',
  quantidadeAceita: 0,
  ...base,
  pn: p.sku,
  produtoId: p.id,
  pilar: p.pilar,
  tipoItem: p.tipoItem,
});

function TabelaComponentesLocacao({ itens, editavel, setItens }: { itens: ComponenteOperacao[]; editavel: boolean; setItens: (c: ComponenteOperacao[]) => void }) {
  const { d } = useProposta();
  const setC = (id: string, patch: Partial<ComponenteOperacao>) => setItens(itens.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const porPn = (pn: string) => d.produtos.find((p) => p.sku && p.sku.toUpperCase() === pn.trim().toUpperCase());
  return (
    <div className="rolagem">
      <table className="tabela tabela-compacta tabela-editor" data-teste="componentes">
        <thead>
          <tr>
            <th>#</th>
            <th className="col-pn">
              PN/SKU *<Ajuda titulo="PN/SKU">Obrigatório na Locação (serviço do catálogo sem PN é escolhido pela Descrição). PN/SKU e Descrição se completam na própria linha; fabricante, pilar e tipo vêm do cadastro de Produtos e Serviços.</Ajuda>
            </th>
            <th className="col-descricao">Descrição</th>
            <th>Fabricante</th>
            <th>Pilar</th>
            <th>Tipo</th>
            <th>Qtd</th>
            {editavel && <th className="col-acoes">Ações</th>}
          </tr>
        </thead>
        <tbody>
          {itens.map((c, n) => {
            const cadastrado = c.pn ? porPn(c.pn) : c.produtoId ? d.produtos.find((p) => p.id === c.produtoId) : undefined;
            const manter = { id: c.id, quantidade: c.quantidade, modelo: c.modelo, observacaoTecnica: c.observacaoTecnica, resultado: c.resultado, quantidadeAceita: c.quantidadeAceita };
            return (
              <tr key={c.id} data-teste={`componente-${n + 1}`}>
                <td>{n + 1}</td>
                {editavel ? (
                  <ProdutoNaLinha
                    pn={c.pn ?? ''}
                    descricao={c.descricao}
                    obrigatorio
                    teste={`produto-componente-${n + 1}`}
                    rotuloLinha={`componente ${n + 1}`}
                    detalhe={!c.pn?.trim() && !cadastrado ? 'PN/SKU obrigatório (ou escolha um serviço do catálogo pela Descrição)' : !cadastrado ? 'não cadastrado' : undefined}
                    aoEscolher={(p) => setC(c.id, componenteDoProduto(p, manter))}
                    aoDigitar={(campo, valor) => {
                      if (campo === 'descricao') return setC(c.id, { descricao: valor });
                      const p = porPn(valor);
                      setC(c.id, p ? componenteDoProduto(p, manter) : { pn: valor, produtoId: undefined });
                    }}
                  />
                ) : (
                  <>
                    <td className="celula-pn">{c.pn || '—'}</td>
                    <td className="descricao-produto">{c.descricao}</td>
                  </>
                )}
                <td>{c.fabricante || '—'}</td>
                <td>{c.pilar || '—'}</td>
                <td>{c.tipoItem || '—'}</td>
                <td>
                  <input type="number" min={1} value={c.quantidade} disabled={!editavel} onChange={(e) => setC(c.id, { quantidade: Math.max(1, Math.floor(Number(e.target.value)) || 1) })} aria-label={`Quantidade do componente ${n + 1}`} className="entrada-curta" />
                </td>
                {editavel && (
                  <td className="col-acoes">
                    <button type="button" className="botao-secundario pequeno" disabled={itens.length === 1} onClick={() => setItens(itens.filter((x) => x.id !== c.id))}>
                      Remover
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Origem do reajuste da PROPOSTA: padrão do parceiro selecionado ou ajustado nesta proposta. */
function OrigemReajuste({ loc }: { loc: DadosLocacao }) {
  const { d } = useProposta();
  const sel = loc.ofertas.find((x) => x.id === loc.selecionadaId);
  const padrao = sel ? grupoDe(d, sel.entidadeId)?.condicoesLocacao?.reajustePadrao : undefined;
  if (!padrao) return <span className="texto-secundario origem-reajuste">sem padrão no parceiro</span>;
  return mesmoReajuste(reajusteDaLocacao(loc), padrao) ? (
    <span className="texto-secundario origem-reajuste" data-teste="origem-reajuste">
      padrão do parceiro
    </span>
  ) : (
    <span className="origem-reajuste ajustado" data-teste="origem-reajuste" title={`Padrão do parceiro: ${textoReajuste(padrao)}`}>
      ajustado nesta proposta <span className="texto-secundario">(padrão: {textoReajuste(padrao)})</span>
    </span>
  );
}

/** Seleciona a proposta do parceiro: traz a regra de comissão e as Condições de Locação padrão do parceiro (sem sobrescrever edições em silêncio). */
function selecionarOferta(d: Base, loc: DadosLocacao, ofertaId: string, autor: string): DadosLocacao {
  const o = loc.ofertas.find((x) => x.id === ofertaId);
  if (!o) return { ...loc, selecionadaId: '' };
  const g = grupoDe(d, o.entidadeId)!;
  const regra: IdRegraComissao = g.comissaoPadrao?.regra && regraComissao(g.comissaoPadrao.regra).baseLocacao ? g.comissaoPadrao.regra : 'por_parcela';
  const manterCondicoes = loc.condicoes?.editadas && loc.condicoes.origemGrupoId !== g.id;
  return {
    ...loc,
    selecionadaId: o.id,
    base: regraComissao(regra).baseLocacao!,
    regraComissao: { regra, percentual: o.percentual, origem: 'herdada do parceiro', parceiroGrupoId: g.id, historico: [...(loc.regraComissao?.historico ?? []), { data: agoraSaoPaulo(), texto: `Regra herdada de ${g.nome}: ${regraComissao(regra).nome}, ${pctNum(o.percentual)}`, autor }] },
    condicoes: manterCondicoes ? loc.condicoes : g.condicoesLocacao ? { ...g.condicoesLocacao, origemGrupoId: g.id, editadas: false } : loc.condicoes,
    ...reajusteAoSelecionar(loc, g, o, autor),
  };
}

/** UMA regra de reajuste por proposta: sem ajuste manual, carrega o padrão do parceiro; com ajuste, mantém. Sempre com histórico. */
function reajusteAoSelecionar(loc: DadosLocacao, g: GrupoRef, o: OfertaLocacao, autor: string): Partial<DadosLocacao> {
  const padrao = g.condicoesLocacao?.reajustePadrao ?? reajusteDaOferta(o);
  const hist = loc.historicoReajuste ?? [];
  if (loc.reajusteAjustado && loc.reajuste && loc.reajusteOrigemGrupoId !== g.id)
    return { reajusteOrigemGrupoId: g.id, historicoReajuste: [...hist, { data: agoraSaoPaulo(), texto: `Parceiro alterado para ${g.nome}: reajuste ajustado mantido (${textoReajuste(loc.reajuste)}); padrão do parceiro: ${textoReajuste(padrao)}`, autor }] };
  if (loc.reajusteOrigemGrupoId === g.id && loc.reajuste) return {};
  return { reajuste: padrao, reajusteOrigemGrupoId: g.id, reajusteAjustado: false, historicoReajuste: [...hist, { data: agoraSaoPaulo(), texto: `Reajuste carregado do padrão de ${g.nome}: ${textoReajuste(padrao)}`, autor }] };
}

/**
 * Revisão 12/10 (P2-26/P2-30 também na Locação): `parte="resumo"` desenha só a Visão interna (no topo, como o Resumo
 * econômico da Venda); `parte="corpo"` desenha o restante. "Comparar" fica no painel único de consultas (`comparar`).
 */
export function EditorLocacao({ v, editavel, set, propostaId, alterado, parte = 'corpo', comparar = false }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void; propostaId: string; alterado?: boolean; parte?: 'resumo' | 'corpo'; comparar?: boolean }) {
  const { d, dados, eu, aplicar } = useProposta();
  const { pode } = useSessao();
  const loc = v.locacao!;
  const setLoc = (patch: Partial<DadosLocacao>) => set({ locacao: { ...loc, ...patch } });
  const setOf = (id: string, patch: Partial<OfertaLocacao>) => setLoc({ ofertas: loc.ofertas.map((o) => (o.id === id ? { ...o, ...patch } : o)) });
  const parceiros = d.entidades.filter((e) => {
    const g = grupoDe(d, e.id);
    return e.situacao === 'Ativa' && g?.situacao === 'Ativo' && g.papeis.includes('Parceiro de Locação');
  });
  const sel = loc.ofertas.find((o) => o.id === loc.selecionadaId);
  const c = calculoLocacao(v);
  const [alterarRegra, setAlterarRegra] = useState(false);
  const entSel = sel ? entidadeDe(d, sel.entidadeId) : undefined;
  const grupoSel = sel ? grupoDe(d, sel.entidadeId) : undefined;
  const abat = abatimentoEfetivo(v.abatimento, dados.opcoes.abatimento); // null = não configurado
  const regra = loc.regraComissao;
  if (parte === 'resumo')
    return (
      <section className="resumo-economico resumo-destaque" data-teste="resumo-locacao" aria-label="Visão interna da Locação">
        <h2>
          Visão interna da Locação 🔒
          <Ajuda titulo="Visão interna da Locação">O Cliente paga exatamente o preço fechado pelo parceiro: a margem da Inttrix NÃO é somada ao valor do Cliente. A margem líquida estimada é interna e não aparece no PDF. Valores previstos: não são recebimento.</Ajuda>
        </h2>
        {c && sel && entSel ? (
          <ResumoIndicadores
            itens={[
              { rotulo: 'Valor da parcela', valor: moeda2(c.clienteParcela) },
              { rotulo: 'Valor total do contrato (Cliente)', valor: moeda2(c.totalContrato) },
              { rotulo: 'Margem bruta total prevista', valor: moeda2(c.comissaoTotal), detalhe: `${moeda2(c.comissaoParcela)} por parcela` },
              { rotulo: abat ? `Margem líquida estimada (abatimento ${pctNum(abat.percentual)})` : 'Margem líquida estimada', valor: abat ? moeda2(liquidaEstimada(c.comissaoTotal, abat.percentual)) : 'abatimento não configurado' },
              { rotulo: 'Líquido total projetado do parceiro', valor: moeda2(c.liquidoTotal) },
            ]}
            secundarios={[
              { rotulo: 'Parceiro selecionado', valor: `${entSel.razao} — ${entSel.cnpj}` },
              { rotulo: 'Prazo · periodicidade · cobranças', valor: `${sel.prazoMeses} meses · ${sel.periodicidade} · ${c.cobrancas}` },
              { rotulo: 'Regra de margem', valor: regra ? `${regraComissao(regra.regra).nome} · ${pctNum(regra.percentual)}` : '—' },
              { rotulo: 'Margem por parcela', valor: `${moeda2(c.comissaoParcela)}${loc.base === 'total' ? ' (equivalência informativa)' : ''}` },
            ]}
          />
        ) : (
          <Estado tipo="vazio" titulo="nenhum parceiro selecionado" />
        )}
      </section>
    );
  return (
    <>
      <fieldset className="form-grade" disabled={!editavel} data-teste="secao-locacao">
        <legend>Projeto de Locação</legend>
        <label className="largura-total">
          Descrição do projeto, solução ou equipamento
          <textarea rows={2} value={loc.descricao} onChange={(e) => setLoc({ descricao: e.target.value })} data-teste="descricao-locacao" />
        </label>
        {/* P3-23/P3-27: sem quantidade geral (a quantidade fica nos itens) e sem datas operacionais no rascunho */}
        <p className="aviso aviso-info largura-total" data-teste="inicio-locacao">
          <strong>Início da locação: conforme a emissão de cada NF de remessa.</strong> Datas efetivas serão registradas na OL.
        </p>
        <CampoData rotulo="Implantação prevista" valor={loc.dataImplantacao ?? ''} aoMudar={(x) => setLoc({ dataImplantacao: x })} teste="data-implantacao" desabilitado={!editavel} />
        <label>
          Prazo de implantação ou disponibilização
          <input value={loc.implantacao} onChange={(e) => setLoc({ implantacao: e.target.value })} />
        </label>
        <label>
          Responsável pela entrega
          <select value={loc.responsavelEntrega || 'A definir'} onChange={(e) => setLoc({ responsavelEntrega: e.target.value })} data-teste="responsavel-entrega">
            {RESPONSAVEIS_OPERACAO.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Responsável pela implantação
          <select value={loc.responsavelImplantacao || 'A definir'} onChange={(e) => setLoc({ responsavelImplantacao: e.target.value })} data-teste="responsavel-implantacao">
            {RESPONSAVEIS_OPERACAO.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="largura-total">
          Observações
          <input value={loc.observacoes} onChange={(e) => setLoc({ observacoes: e.target.value })} />
        </label>
      </fieldset>

      <section className="secao-editor" data-teste="itens-locacao">
        <div className="barra-titulo">
          <h2>Itens e componentes do projeto</h2>
          {editavel && (
            <div className="acoes-direita">
              <button type="button" className="botao-primario" onClick={() => setLoc({ componentes: [...loc.componentes, { id: `cp${Date.now() % 100000}-${++seq}`, descricao: '', categoria: '', fabricante: '', modelo: '', quantidade: 1, condicao: '', ano: '', lote: '', resultado: 'pendente', quantidadeAceita: 0, pn: '' }] })} data-teste="adicionar-componente">
                Adicionar componente
              </button>
            </div>
          )}
        </div>
        <TabelaComponentesLocacao itens={loc.componentes} editavel={editavel} setItens={(x) => setLoc({ componentes: x })} />
      </section>

      <section className="secao-editor" data-teste="cotacoes">
        <div className="barra-titulo">
          <h2>
            Propostas de parceiros de Locação
            <Ajuda titulo="Propostas de parceiros">O parceiro informa o valor que o Cliente pagará, com a margem da Inttrix já incluída. Compare e selecione uma: só a selecionada alimenta cálculos e documentos e fica congelada na versão aceita.</Ajuda>
          </h2>
          <div className="acoes-direita">
            {editavel && (
              <button
                type="button"
                className="botao-secundario"
                disabled={!parceiros.length}
                title={!parceiros.length ? 'Cadastre um parceiro com o papel Parceiro de Locação' : undefined}
                onClick={() => {
                  const e = parceiros[0];
                  const g = grupoDe(d, e.id)!;
                  const reaj = g.condicoesLocacao?.reajustePadrao ?? { tipo: 'Índice' as const, indice: '', periodicidade: 'Anual' };
                  setLoc({ ofertas: [...loc.ofertas, { id: `ol${Date.now() % 100000}-${++seq}`, entidadeId: e.id, valorParcela: 0, periodicidade: 'Mensal', prazoMeses: 12, percentual: g.comissaoPadrao?.percentual ?? 10, ...comReajuste(reaj), implantacao: '', validadeDias: 15, observacao: '' }] });
                }}
                data-teste="adicionar-oferta"
              >
                Adicionar proposta de parceiro
              </button>
            )}
          </div>
        </div>
        <p className={`parceiro-selecionado${entSel ? '' : ' pendente'}`} data-teste="parceiro-locacao-selecionado">
          {entSel ? (
            <>
              Parceiro de Locação selecionado: <strong>{entSel.razao}</strong> — {entSel.cnpj} {grupoSel?.nome && grupoSel.nome !== entSel.razao && <span className="texto-secundario">({grupoSel.nome})</span>}
            </>
          ) : (
            'Nenhum parceiro de Locação selecionado: o rascunho pode ser salvo, mas o envio fica bloqueado.'
          )}
        </p>
        <div className="rolagem">
          <table className="tabela tabela-compacta tabela-editor" data-teste="tabela-ofertas">
            <thead>
              <tr>
                <th>Parceiro comercial · entidade/CNPJ</th>
                <th>Valor da parcela</th>
                <th>Periodicidade</th>
                <th>Prazo (meses)</th>
                <th>Valor total</th>
                <th>% Margem</th>
                <th>Margem</th>
                <th>Selecionada</th>
                {editavel && <th className="col-acoes">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loc.ofertas.map((o, k) => {
                const x = calcularLocacao(o, loc.base);
                const menor = Math.min(...loc.ofertas.map((y) => calcularLocacao(y, loc.base).totalContrato));
                const ent = entidadeDe(d, o.entidadeId);
                return (
                  <tr key={o.id} className={o.id === loc.selecionadaId ? 'linha-escolhida' : ''} data-teste={`oferta-${k + 1}`}>
                    <td>
                      <select value={o.entidadeId} disabled={!editavel || o.id === loc.selecionadaId} onChange={(e) => setOf(o.id, { entidadeId: e.target.value })} aria-label={`Parceiro da proposta ${k + 1}`} data-teste={`parceiro-oferta-${k + 1}`}>
                        {[...parceiros, ...(ent && !parceiros.includes(ent) ? [ent] : [])].map((p) => (
                          <option key={p.id} value={p.id}>
                            {/* sem repetir o nome quando grupo e entidade são iguais */}
                            {grupoDe(d, p.id)?.nome && grupoDe(d, p.id)!.nome !== p.fantasia ? `${grupoDe(d, p.id)!.nome} · ${p.fantasia}` : p.fantasia}
                          </option>
                        ))}
                      </select>
                      <div className="texto-secundario nowrap">{ent?.cnpj}</div>
                    </td>
                    <td>
                      <input type="number" min={0} step="0.01" value={o.valorParcela} disabled={!editavel} onChange={(e) => setOf(o.id, { valorParcela: Number(e.target.value) })} aria-label={`Valor da parcela da proposta ${k + 1}`} className="entrada-media" data-teste={`parcela-oferta-${k + 1}`} />
                    </td>
                    <td>
                      <select value={o.periodicidade} disabled={!editavel} onChange={(e) => setOf(o.id, { periodicidade: e.target.value as Periodicidade })} aria-label={`Periodicidade da proposta ${k + 1}`}>
                        {PERIODICIDADES.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input type="number" min={1} value={o.prazoMeses} disabled={!editavel} onChange={(e) => setOf(o.id, { prazoMeses: Math.floor(Number(e.target.value)) || 0 })} aria-label={`Prazo da proposta ${k + 1}`} className="entrada-curta" data-teste={`prazo-oferta-${k + 1}`} />
                    </td>
                    <td className="nowrap">
                      {moeda2(x.totalContrato)}
                      {comparar && x.totalContrato === menor && <div className="diferenca-positiva">menor valor ao Cliente</div>}
                    </td>
                    <td>
                      <input type="number" min={0} max={99} step="0.1" value={o.percentual} disabled={!editavel || o.id === loc.selecionadaId} title={o.id === loc.selecionadaId ? 'Na proposta selecionada, altere pela regra de margem (autorizada)' : undefined} onChange={(e) => setOf(o.id, { percentual: Number(e.target.value) })} aria-label={`Percentual de margem da proposta ${k + 1}`} className="entrada-curta" />
                    </td>
                    <td className="nowrap">
                      {moeda2(x.comissaoTotal)}
                      <div className="texto-secundario">{moeda2(x.comissaoParcela)}/parcela</div>
                    </td>
                    <td>
                      <label className="opcao">
                        <input type="radio" name="oferta-sel" checked={o.id === loc.selecionadaId} disabled={!editavel} onChange={() => setLoc(selecionarOferta(d, loc, o.id, eu))} aria-label={`Selecionar a proposta ${k + 1}`} data-teste={`selecionar-oferta-${k + 1}`} />
                        {o.id === loc.selecionadaId ? 'Selecionada' : 'Selecionar'}
                      </label>
                      {x.erros.length > 0 && <div className="diferenca-negativa">{x.erros[0]}</div>}
                    </td>
                    {editavel && (
                      <td className="col-acoes">
                        <button type="button" className="botao-secundario pequeno" onClick={() => setLoc({ ofertas: [...loc.ofertas.slice(0, k + 1), { ...o, id: `ol${Date.now() % 100000}-${++seq}` }, ...loc.ofertas.slice(k + 1)] })}>
                          Duplicar
                        </button>{' '}
                        <button type="button" className="botao-secundario pequeno" onClick={() => setLoc({ ofertas: loc.ofertas.filter((y) => y.id !== o.id), selecionadaId: loc.selecionadaId === o.id ? '' : loc.selecionadaId, ...(loc.selecionadaId === o.id ? { regraComissao: undefined } : {}) })}>
                          Remover
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {loc.ofertas.length === 0 && (
                <tr>
                  <td colSpan={10}>
                    <Estado tipo="vazio" titulo="nenhuma proposta de parceiro — use Adicionar proposta de parceiro" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <fieldset className="form-grade" disabled={!editavel} data-teste="condicoes-locacao">
        <legend>Condições financeiras da Locação {sel ? `(proposta selecionada: ${entSel?.fantasia})` : ''}</legend>
        {sel && c ? (
          <>
            <label>
              Prazo contratual (meses)
              <input type="number" min={1} value={sel.prazoMeses} onChange={(e) => setOf(sel.id, { prazoMeses: Math.floor(Number(e.target.value)) || 0 })} data-teste="prazo-contratual" />
            </label>
            <label>
              Periodicidade
              <select value={sel.periodicidade} onChange={(e) => setOf(sel.id, { periodicidade: e.target.value as Periodicidade })} data-teste="periodicidade">
                {PERIODICIDADES.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Valor da parcela (R$)
              <input type="number" min={0} step="0.01" value={sel.valorParcela} onChange={(e) => setOf(sel.id, { valorParcela: Number(e.target.value) })} data-teste="valor-parcela" />
            </label>
            <label>
              Quantidade de cobranças
              <Ajuda titulo="Cobranças">Derivada do prazo contratual e da periodicidade (mensal: uma por mês). Não existe campo “parcelas” separado.</Ajuda>
              <input value={c.erros.some((x) => /múltiplo/.test(x)) ? '—' : c.cobrancas} readOnly data-teste="cobrancas" />
            </label>
            <label>
              Valor total do contrato (calculado)
              <input value={moeda2(c.totalContrato)} readOnly data-teste="total-contrato" />
            </label>
            <label>
              Forma de pagamento
              <input value={loc.formaPagamento} onChange={(e) => setLoc({ formaPagamento: e.target.value })} />
            </label>
            <div className="largura-total bloco-reajuste" data-teste="reajuste-selecionada">
              <div className="subtitulo-form">
                Reajuste da proposta <OrigemReajuste loc={loc} />
              </div>
              <EditorReajuste
                valor={reajusteDaLocacao(loc) ?? { tipo: 'Sem reajuste' }}
                aoMudar={(r) => {
                  const g = grupoDe(d, sel.entidadeId)!;
                  const padrao = g.condicoesLocacao?.reajustePadrao;
                  const ajustado = !padrao || !mesmoReajuste(r, padrao);
                  const registrar = ajustado && !loc.reajusteAjustado;
                  setLoc({ reajuste: r, reajusteOrigemGrupoId: g.id, reajusteAjustado: ajustado, historicoReajuste: registrar ? [...(loc.historicoReajuste ?? []), { data: agoraSaoPaulo(), texto: 'Reajuste ajustado nesta proposta (diferente do padrão do parceiro)', autor: eu }] : loc.historicoReajuste });
                }}
                teste="reajuste-selecionado"
                desabilitado={!editavel}
              />
              <p className="texto-secundario sem-margem" data-teste="texto-reajuste-selecionado">
                Uma única regra para toda a proposta. Vai para a Proposta e o PDF como: <strong>{textoReajuste(reajusteDaLocacao(loc))}</strong>
              </p>
              {loc.reajusteAjustado && grupoSel?.condicoesLocacao?.reajustePadrao && editavel && (
                <button
                  type="button"
                  className="botao-link"
                  onClick={() => {
                    const padrao = grupoSel.condicoesLocacao!.reajustePadrao!;
                    setLoc({ reajuste: padrao, reajusteOrigemGrupoId: grupoSel.id, reajusteAjustado: false, historicoReajuste: [...(loc.historicoReajuste ?? []), { data: agoraSaoPaulo(), texto: `Padrão de ${grupoSel.nome} aplicado: ${textoReajuste(padrao)}`, autor: eu }] });
                  }}
                  data-teste="aplicar-padrao-reajuste"
                >
                  Aplicar o padrão de {grupoSel.nome}
                </button>
              )}
              {(loc.historicoReajuste ?? []).length > 0 && (
                <ul className="linha-tempo historico-reajuste" data-teste="historico-reajuste">
                  {loc.historicoReajuste!.map((h, i) => (
                    <li key={i} className="texto-pequeno">
                      <strong>{dataBr(h.data.slice(0, 10))}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="largura-total regra-comissao" data-teste="regra-comissao">
              <span>
                <strong>Regra de margem:</strong> {regra ? `${regraComissao(regra.regra).nome} · ${pctNum(regra.percentual)}` : '—'}{' '}
                <span className="texto-secundario">
                  (base: {regra ? regraComissao(regra.regra).base.toLowerCase() : '—'}; frequência: {regra ? regraComissao(regra.regra).frequencia.toLowerCase() : '—'}) · {regra?.origem ?? 'sem regra'}
                </span>
              </span>
              {editavel && regra && pode('alterar_comissao', 'proposta') && (
                <button type="button" className="botao-secundario pequeno" disabled={alterado} title={alterado ? 'Salve a versão antes de alterar a regra' : undefined} onClick={() => setAlterarRegra(true)} data-teste="alterar-regra">
                  Alterar regra (autorizada) 🔒
                </button>
              )}
            </div>
            {c.erros.length > 0 && <Estado tipo="erro" titulo="corrija as condições financeiras" itens={c.erros} teste="erros-locacao" />}
            <p className="texto-secundario largura-total">
              {sel.prazoMeses} meses · {sel.periodicidade.toLowerCase()} → {c.cobrancas} cobrança(s). Vencimentos conforme a regra do parceiro, a partir da NF de remessa (registrados na OL).
            </p>
          </>
        ) : (
          <Estado tipo="bloqueado" titulo="Selecione um parceiro de Locação para definir prazo, periodicidade, parcela e margem" />
        )}
      </fieldset>

      <fieldset className="form-grade" disabled={!editavel} data-teste="condicoes-de-locacao">
        <legend>
          Condições de Locação{' '}
          {loc.condicoes?.origemGrupoId && (
            <span className="selo selo-neutro" data-teste="origem-condicoes">
              {loc.condicoes.editadas ? 'editadas nesta versão' : `herdadas de ${d.grupos.find((g) => g.id === loc.condicoes!.origemGrupoId)?.nome}`}
            </span>
          )}
        </legend>
        {grupoSel && loc.condicoes?.origemGrupoId !== grupoSel.id && grupoSel.condicoesLocacao && (
          <p className="aviso aviso-alerta largura-total" data-teste="condicoes-de-outro-parceiro">
            <span className="rotulo-nivel">▲ Atenção</span>As condições abaixo foram editadas com outro parceiro e foram mantidas (nada é sobrescrito em silêncio).{' '}
            {editavel && (
              <button type="button" className="botao-link" onClick={() => setLoc({ condicoes: { ...grupoSel.condicoesLocacao!, origemGrupoId: grupoSel.id, editadas: false } })}>
                Substituir pelas condições padrão de {grupoSel.nome}
              </button>
            )}
          </p>
        )}
        {CAMPOS_CONDICOES_LOCACAO.filter((f) => f.chave !== 'reajuste').map((f) => (
          <label key={f.chave} className={f.chave === 'observacoes' || f.chave === 'responsabilidades' ? 'largura-total' : 'coluna-dupla'}>
            {f.rotulo}
            <input value={loc.condicoes?.[f.chave] ?? ''} onChange={(e) => setLoc({ condicoes: { ...(loc.condicoes ?? { responsabilidades: '', implantacao: '', manutencao: '', substituicao: '', seguro: '', cancelamento: '', renovacao: '', reajuste: '', observacoes: '' }), [f.chave]: e.target.value, editadas: true } })} data-teste={`condicao-locacao-${f.chave}`} />
          </label>
        ))}
        {editavel && grupoSel?.condicoesLocacao && loc.condicoes?.editadas && loc.condicoes.origemGrupoId === grupoSel.id && (
          <div className="largura-total">
            <button type="button" className="botao-secundario pequeno" onClick={() => setLoc({ condicoes: { ...grupoSel.condicoesLocacao!, origemGrupoId: grupoSel.id, editadas: false } })}>
              Restaurar as condições padrão do parceiro
            </button>
          </div>
        )}
      </fieldset>

      {alterarRegra && regra && (
        <ModalRegra
          loc={loc}
          aoFechar={() => setAlterarRegra(false)}
          aoAplicar={async (nova, justificativa) => {
            await api.post(`/api/propostas/${propostaId}/regra-comissao`, { ...nova, justificativa });
            aplicar(await api.get<DadosProposta>(`/api/oportunidades/${dados.oportunidade.id}/proposta`));
          }}
        />
      )}
      <Nota>Somente a proposta selecionada alimenta os cálculos e os documentos. Na versão aceita, parceiro, condições financeiras, regra de margem e Condições de Locação ficam congelados.</Nota>
    </>
  );
}

function ModalRegra({ loc, aoFechar, aoAplicar }: { loc: DadosLocacao; aoFechar: () => void; aoAplicar: (r: { regra: IdRegraComissao; percentual: number }, justificativa: string) => Promise<void> }) {
  const atual = loc.regraComissao!;
  const [regra, setRegra] = useState<IdRegraComissao>(atual.regra);
  const [percentual, setPercentual] = useState(atual.percentual);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const ok = justificativa.trim().length > 3 && percentual >= 0 && percentual < 100 && (regra !== atual.regra || percentual !== atual.percentual);
  const aplicar = async () => {
    try {
      await aoAplicar({ regra, percentual }, justificativa.trim());
      aoFechar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Alterar a regra de comissão (ação autorizada)"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={() => void aplicar()} data-teste="aplicar-regra">
            Aplicar alteração
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">Ação sensível (permissão: alterar comissão). A alteração fica no histórico da versão e na auditoria; a versão aceita congela a regra.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Regra
        <select value={regra} onChange={(e) => setRegra(e.target.value as IdRegraComissao)} data-teste="nova-regra">
          {REGRAS_COMISSAO.filter((r) => r.baseLocacao).map((r) => (
            <option key={r.id} value={r.id}>
              {r.nome}
            </option>
          ))}
        </select>
      </label>
      <p className="texto-secundario">
        Base: {regraComissao(regra).base} · Frequência: {regraComissao(regra).frequencia}
      </p>
      <label className="campo">
        Percentual (%)
        <input type="number" min={0} max={99} step="0.1" value={percentual} onChange={(e) => setPercentual(Number(e.target.value))} data-teste="novo-percentual" />
      </label>
      <label className="campo">
        Justificativa (obrigatória)
        <input value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-regra" />
      </label>
      <h3>Histórico da regra</h3>
      <ul className="linha-tempo">
        {atual.historico.map((h, i) => (
          <li key={i}>
            <strong>{dataBr(h.data.slice(0, 10))}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
